import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { X, Check, Package, Trash2, Plus } from "lucide-react";
import "./ReceptionStockModal.css";

const ReceptionStockModal = ({ show, onClose, onSuccess, ingredients = [] }) => {
    const MySwal = withReactContent(Swal);
    const [loading, setLoading] = useState(false);
    const [receptionLines, setReceptionLines] = useState([
        { id: Date.now(), id_ingredient: "", quantite: "", note: "" }
    ]);

    const token = JSON.parse(localStorage.getItem("user"))?.access_token;

    // Réinitialiser le formulaire quand le modal s'ouvre
    useEffect(() => {
        if (show) {
            setReceptionLines([
                { id: Date.now(), id_ingredient: "", quantite: "", note: "" }
            ]);
        }
    }, [show]);

    const handleLineChange = (lineId, field, value) => {
        setReceptionLines(prevLines =>
            prevLines.map(line =>
                line.id === lineId
                    ? { ...line, [field]: value }
                    : line
            )
        );
    };

    const addLine = () => {
        setReceptionLines(prevLines => [
            ...prevLines,
            { id: Date.now(), id_ingredient: "", quantite: "", note: "" }
        ]);
    };

    const removeLine = (lineId) => {
        if (receptionLines.length === 1) {
            MySwal.fire({
                icon: "warning",
                text: "Vous devez avoir au moins une ligne.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
            return;
        }
        setReceptionLines(prevLines => prevLines.filter(line => line.id !== lineId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation : au moins une ligne avec ingrédient et quantité
        const validLines = receptionLines.filter(
            line => line.id_ingredient && line.quantite && parseFloat(line.quantite) > 0
        );

        if (validLines.length === 0) {
            MySwal.fire({
                icon: "error",
                text: "Veuillez renseigner au moins un ingrédient avec une quantité valide.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
            return;
        }

        setLoading(true);

        try {
            // Envoyer chaque ligne de réception comme un mouvement d'ajout
            const promises = validLines.map(line => {
                const payload = {
                    id_ingredient: parseInt(line.id_ingredient),
                    quantite: parseFloat(line.quantite),
                    type_mouvement: "AJOUT",
                    commentaire: line.note || "Réception de stock"
                };

                return axios.post(
                    `${base_api_url}api/stock/mouvement`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            });

            const results = await Promise.all(promises);
            console.log("Réceptions effectuées:", results);

            // Show success toast
            MySwal.fire({
                icon: "success",
                text: `${validLines.length} réception(s) de stock effectuée(s) avec succès.`,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });

            // Reset et fermer
            setReceptionLines([
                { id: Date.now(), id_ingredient: "", quantite: "", note: "" }
            ]);
            onSuccess();
            onClose();

        } catch (error) {
            console.error("Error during stock reception:", error);

            let errorMessage = "Une erreur s'est produite lors de la réception du stock.";

            if (error.response) {
                const status = error.response.status;
                const errorData = error.response.data;

                switch (status) {
                    case 400:
                        errorMessage = errorData?.message || "Données invalides. Vérifiez les champs.";
                        break;
                    case 401:
                        errorMessage = "Session expirée. Veuillez vous reconnecter.";
                        break;
                    case 404:
                        errorMessage = "Un ou plusieurs ingrédients n'existent pas.";
                        break;
                    case 500:
                        errorMessage = "Erreur serveur. Veuillez réessayer.";
                        break;
                    default:
                        errorMessage = errorData?.message || `Erreur ${status} lors de l'opération.`;
                }
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
            }

            MySwal.fire({
                icon: "error",
                text: errorMessage,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    // Obtenir l'unité d'un ingrédient sélectionné
    const getIngredientUnit = useCallback((ingredientId) => {
        const ingredient = ingredients.find(ing => ing.id_ingredient === parseInt(ingredientId));
        return ingredient?.unite || "g";
    }, [ingredients]);

    if (!show) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`reception-stock-backdrop ${show ? 'show' : ''}`}
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className={`reception-stock-modal ${show ? 'show' : ''}`}>
                <form onSubmit={handleSubmit}>
                    {/* Header */}
                    <div className="reception-stock-header">
                        <div className="reception-stock-header-content">
                            <Package size={24} />
                            <h4>Réception de stock</h4>
                        </div>
                        <button
                            type="button"
                            className="reception-stock-close-btn"
                            onClick={onClose}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Subtitle */}
                    <div className="reception-stock-subtitle">
                        Ajoutez plusieurs ingrédients en une seule opération
                    </div>

                    {/* Body */}
                    <div className="reception-stock-body">
                        {/* Table Header */}
                        <div className="reception-stock-table-header">
                            <div className="reception-stock-col-ingredient">Ingrédient</div>
                            <div className="reception-stock-col-quantity">Quantité</div>
                            <div className="reception-stock-col-note">Note (optionnel)</div>
                            <div className="reception-stock-col-actions"></div>
                        </div>

                        {/* Lines */}
                        <div className="reception-stock-lines">
                            {receptionLines.map((line) => (
                                <div key={line.id} className="reception-stock-line">
                                    {/* Ingrédient Select */}
                                    <div className="reception-stock-col-ingredient">
                                        <select
                                            value={line.id_ingredient}
                                            onChange={(e) => handleLineChange(line.id, "id_ingredient", e.target.value)}
                                            required
                                            className="reception-stock-select"
                                        >
                                            <option value="">Sélectionner...</option>
                                            {ingredients.map(ing => (
                                                <option key={ing.id_ingredient} value={ing.id_ingredient}>
                                                    {ing.nom} ({ing.unite})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Quantité Input */}
                                    <div className="reception-stock-col-quantity">
                                        <div className="reception-stock-quantity-wrapper">
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                value={line.quantite}
                                                onChange={(e) => handleLineChange(line.id, "quantite", e.target.value)}
                                                required
                                                className="reception-stock-input"
                                                placeholder="0"
                                            />
                                            <span className="reception-stock-unit">
                                                {line.id_ingredient ? getIngredientUnit(line.id_ingredient) : "g"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Note Input */}
                                    <div className="reception-stock-col-note">
                                        <input
                                            type="text"
                                            value={line.note}
                                            onChange={(e) => handleLineChange(line.id, "note", e.target.value)}
                                            className="reception-stock-input"
                                            placeholder="Ex: Lot #123"
                                        />
                                    </div>

                                    {/* Delete Button */}
                                    <div className="reception-stock-col-actions">
                                        <button
                                            type="button"
                                            className="reception-stock-delete-btn"
                                            onClick={() => removeLine(line.id)}
                                            title="Supprimer la ligne"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Add Line Button */}
                        <button
                            type="button"
                            className="reception-stock-add-line-btn"
                            onClick={addLine}
                        >
                            <Plus size={16} />
                            Ajouter une ligne
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="reception-stock-footer">
                        <button
                            type="button"
                            className="reception-stock-btn reception-stock-btn-secondary"
                            onClick={onClose}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="reception-stock-btn reception-stock-btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="reception-stock-spinner"></span>
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <Check size={18} />
                                    Valider la réception
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

ReceptionStockModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    ingredients: PropTypes.array,
};

export default ReceptionStockModal;
